import { defineStore } from 'pinia'
import { CustomerModel } from '@simploshop-models/customer.model'
import { MeModel } from '@simploshop-models/me.model'
import type { UpdateCustomerPayload } from '@simploshop-services/Customers.service'
import { ME_MODEL_EMBEDS } from '../assets/ts/constants/customer'

export const useAuthStore = defineStore('auth', () => {
    const appConfig = useAppConfig()
    const nuxtApp = useNuxtApp()
    const { notifyError } = useNotifications()
    const _meModel = ref<InstanceType<typeof MeModel> | null | undefined>(undefined)

    // public properties
    const isLoggedIn = computed(() => !!(_meModel.value?.customer))
    /**
     * Indicates that the login process is in progress, however, the login request to the BE has already been successfully made.
     */
    const loggingInMeModel = ref(null) as Ref<InstanceType<typeof MeModel> | null>
    /**
     * Indicates that the logout process is in progress, however, the logout request to the BE has already been successfully made.
     */
    const isLoggingOut = ref<boolean>(false)
    const syncedCustomer = computed(() => _meModel.value?.customer ?? null) as ComputedRef<InstanceType<typeof CustomerModel> | null>
    const customer = computed(() => loggingInMeModel.value?.customer ?? syncedCustomer.value) as ComputedRef<InstanceType<typeof CustomerModel> | null>

    const syncedMeModel = computed(() => _meModel.value ?? null) as ComputedRef<InstanceType<typeof MeModel> | null>
    const meModel = computed(() => loggingInMeModel.value ?? syncedMeModel.value) as ComputedRef<InstanceType<typeof MeModel> | null>

    /**
     * Set the customer or me model.
     * If the visitor is a guest (not logged-in), the customer will be set to `null`
     *
     * @param val the `MeModel` or `CustomerModel` to set
     */
    function setCustomer(val: MeModel | CustomerModel | null) {
        // if a MeModel is passed, set it directly
        if (val instanceof MeModel || val === null) {
            _meModel.value = val
            return
        }

        // if a CustomerModel is passed, update it in the existing MeModel
        if (!_meModel.value) return
        _meModel.value.customer = val
    }

    function removeCustomer() {
        setCustomer(null)
    }

    const customerDataToUpdate = ref<UpdateCustomerPayload>({})

    const { error, execute } = useCustomersApiService()
        .embed([
            // @ts-ignore - TODO: fix app config type
            ...((appConfig.embeds?.customer ?? []) as any[]),
        ])
        .forId(() => customer.value?.id)
        .usePatch(customerDataToUpdate, {
            immediate: false,
            watch: false,
            onResponse: (response) => {
                const customer = response.getItem()
                if (!customer) {
                    notifyError(nuxtApp.$i18n.t('_core_theme.errors.customer_update_error'))
                    return
                }
                setCustomer(customer)
            },
        })

    async function updateCustomer(data: UpdateCustomerPayload) {
        if (!isLoggedIn.value) return

        customerDataToUpdate.value = data

        await execute()

        customerDataToUpdate.value = {}

        if (error.value) {
            throw error.value
        }
    }

    /**
     * Fetches the current user and sets it in the auth store.
     * @returns `true` if the user is a guest, `false` otherwise
     * @throws Error if the API request fails
     */
    async function _fetchMe() {
        const response = await getMeApiService()
            .embed([
                ...ME_MODEL_EMBEDS,
                [MeModel.EMBED_CUSTOMER, {
                    // @ts-ignore - TODO: fix app config type
                    embed: (appConfig.embeds?.customer ?? []) as any[],
                }],
            ])
            .exceptAttrs([MeModel.ATTR_CUSTOMER])
            .get()

        return response.getItem()
    }

    async function refreshCustomer() {
        if (!isLoggedIn.value) return

        let meModel: MeModel | null = null

        try {
            meModel = await _fetchMe()
        } catch (e) {
            console.error('Failed to refresh customer:', e)
        }

        if (!meModel) {
            notifyError(nuxtApp.$i18n.t('_core_theme.errors.failed_to_refresh_customer'))
            return
        }

        setCustomer(meModel)
    }

    return {
        _meModel, // Needs to be returned to be sent in the payload
        meModel,
        syncedMeModel,
        customer,
        syncedCustomer,
        isLoggedIn,
        loggingInMeModel,
        isLoggingOut,
        updateCustomer,
        _setCustomer: setCustomer,
        _fetchMe: _fetchMe,
        refreshCustomer,
        removeCustomer,
    }
})
